"use client";

import { FC, MouseEvent, useMemo, useState } from "react";
import { ProductResource } from "@/api/generated-api";
import { getLocalPrice } from "@/utils/formatters";
import { useStrictLocale } from "@/hooks/locale/useStrictLocale/useStrictLocale";
import { useStore } from "@/hooks/system/useStore";
import { observer } from "mobx-react-lite";
import CustomIcon from "@/components/ui/custom-icon";
import Link from "next/link";
import Image from "next/image";
import S from "./index.module.scss";
import clsx from "clsx";

interface IProductCard {
  product: ProductResource;
}

const ProductCard: FC<IProductCard> = ({ product }) => {
  const [activeVariation, setActiveVariation] = useState(product);
  const { preview_image, collection_title, title, price, guid } = activeVariation;
  const { changeFavorite, isInFavorites } = useStore("favorite");
  const isFavorite = isInFavorites(guid);

  const locale = useStrictLocale();

  const colors = useMemo(() => {
    return product.product_variation?.variants?.[0]?.variants || [];
  }, [product]);

  const chooseVariation = (e: MouseEvent<HTMLElement>, activeVar: ProductResource | null) => {
    e.stopPropagation();
    e.preventDefault();
    if (activeVar) {
      setActiveVariation(activeVar);
    }
  };

  const handleChangeFavorite = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    changeFavorite(guid);
  };

  const localePrice = getLocalPrice(locale);

  return (
    <Link className={S.card} href={`/products/${guid}`}>
      {isFavorite && (
        <button className={S.favoriteButton} onClick={handleChangeFavorite}>
          <CustomIcon fileName="icon-heart-black-active" />
        </button>
      )}
      <Image className={S.cardImage} width={420} height={420} src={preview_image} alt={`Product ${title}`} />
      <div className={S.cardInfo}>
        <span className={S.cardName}>{title}</span>
        <div className={S.bottomContentWrap}>
          <div className={S.variationsContainer}>
            {colors.map(variant => (
              <button
                className={clsx(S.variationButton, {
                  [S["variationButton--active"]]: variant.product?.guid === activeVariation.guid,
                })}
                key={variant.title}
                onClick={e => chooseVariation(e, variant.product)}
              >
                <Image
                  className={S.variationImage}
                  width={32}
                  height={32}
                  src={variant.image}
                  alt={`Color ${variant.colour_code}`}
                />
              </button>
            ))}
          </div>
          {price && (
            <div className={S.priceWrapper}>
              <span className={S.oldPrice}>{`${price.base_price} ${localePrice}`}</span>
              <span className={S.newPrice}>{`${price.final_price} ${localePrice}`}</span>
            </div>
          )}
          <span className={S.collectionName}>{collection_title}</span>
        </div>
      </div>
    </Link>
  );
};

export default observer(ProductCard);
